import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/lib/api';

export type ProjectStatus =
  | 'planning'
  | 'construction'
  | 'selling'
  | 'sold_out'
  | 'delivered'
  | 'archived';

export type Project = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  address: string;
  city: string;
  currency: string;
  status: ProjectStatus;
  startDate: string | null;
  plannedDeliveryDate: string | null;
  defaultReservationHours: number;
  defaultLateFeePercent: string;
  defaultGracePeriodDays: number;
  coverImageUrl: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  _count?: { blocks: number; apartments: number };
};

type ListResponse = {
  data: Project[];
  pagination: { cursor: string | null; hasMore: boolean; limit: number; total: number };
};

export type CreateProjectInput = {
  code: string;
  name: string;
  description?: string;
  address: string;
  city?: string;
  status?: ProjectStatus;
  startDate?: string;
  plannedDeliveryDate?: string;
  defaultReservationHours?: number;
};

export function useProjectsList(params?: { search?: string; status?: ProjectStatus }) {
  return useQuery({
    queryKey: ['projects', params],
    queryFn: async () => {
      const res = await api.get<ListResponse>('/projects', { params });
      return res.data;
    },
  });
}

export function useProject(id: string | undefined) {
  return useQuery({
    queryKey: ['project', id],
    enabled: !!id,
    queryFn: async () => {
      const res = await api.get<{ data: Project }>(`/projects/${id}`);
      return res.data.data;
    },
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateProjectInput) => {
      const res = await api.post<{ data: Project }>('/projects', input);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: Partial<CreateProjectInput> & { status?: ProjectStatus; description?: string; city?: string } }) => {
      const res = await api.patch<{ data: Project }>(`/projects/${id}`, input);
      return res.data.data;
    },
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['project', id] });
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/projects/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}
